import { computed, onMounted, ref, watch } from "vue";
import { useState } from "#app";
import {
	getCoordinates,
	getMerchantList,
	getMerchantRegionsByLocation,
	getMerchantShowDetail,
} from "~/api/api";
import { useUserLocationStore } from "~/stores/user-location";

const SERVICE_INSTITUTION_CACHE_KEY = "seer-service-institution-cache-v1";

export function useServiceInstitutionCache() {
	return useState(SERVICE_INSTITUTION_CACHE_KEY, () => ({
		merchantTypes: {},
		merchants: {},
		details: {},
		regions: {},
	}));
}

function hasCachedValue(cache, key) {
	return Object.prototype.hasOwnProperty.call(cache, key);
}

function getLocaleCacheKey(locale) {
	return String(locale?.value || locale || "zh-TW");
}

function getRequestCacheKey(locale, params) {
	return JSON.stringify({
		locale: getLocaleCacheKey(locale),
		...params,
	});
}

export function parseJsonField(value) {
	if (!value) return [];
	if (Array.isArray(value)) return value;
	try {
		return JSON.parse(value);
	} catch {
		return [];
	}
}

export function formatDistance(meters) {
	if (meters == null || Number.isNaN(Number(meters))) return "";
	const distance = Number(meters);
	if (distance >= 1000) return `${(distance / 1000).toFixed(1)}km`;
	return `${Math.round(distance)}m`;
}

function getMerchantTypeName(merchantTypes, merchantTypeId) {
	const matched = merchantTypes.find(item => String(item.id) === String(merchantTypeId));
	return matched?.name || "";
}

function mapMerchantToListItem(item, merchantTypes, t) {
	const collectNum = Number(item.collect_num ?? 0);
	const distanceKm =
		item.distance == null || Number.isNaN(Number(item.distance))
			? undefined
			: Number((Number(item.distance) / 1000).toFixed(2));
	return {
		id: item.id,
		name: item.name || "",
		type: getMerchantTypeName(merchantTypes, item.merchant_type),
		rating: item.score || "0",
		reviews: String(item.evaluation_num ?? 0),
		address: item.address || "",
		desc: item.description || "",
		favorites:
			collectNum > 0
				? t("serviceInstitution.pc.demo.search.favoritesCount", { count: collectNum })
				: "",
		distance: formatDistance(item.distance),
		distanceKm,
		cover: item.cover || "",
		lat: item.lat,
		lng: item.lng,
	};
}

function stripHtml(html) {
	if (!html) return "";
	return String(html)
		.replace(/<[^>]*>/g, "")
		.trim();
}

function getMerchantPhotos(item) {
	const photos = [];
	const seen = new Set();

	const addPhoto = url => {
		if (url && !seen.has(url)) {
			seen.add(url);
			photos.push(url);
		}
	};

	addPhoto(item.cover);
	if (Array.isArray(item.media?.image)) {
		item.media.image.forEach(addPhoto);
	}

	return photos;
}

function parseMerchantMedia(media) {
	if (!media) return { images: [], videos: [] };

	let mediaData = media;
	if (typeof mediaData === "string") {
		try {
			mediaData = JSON.parse(mediaData);
		} catch {
			return { images: [], videos: [] };
		}
	}

	if (!Array.isArray(mediaData)) return { images: [], videos: [] };

	const images = [];
	const videos = [];

	const appendMedia = mediaItem => {
		if (!mediaItem || typeof mediaItem !== "object") return;
		const type = Number(mediaItem.type);
		const url = mediaItem.url || "";
		if (!url) return;
		if (type === 1) images.push(url);
		if (type === 2) videos.push(url);
	};

	mediaData.forEach(item => {
		if (Array.isArray(item)) {
			item.forEach(appendMedia);
			return;
		}
		appendMedia(item);
	});

	return { images, videos };
}

function parseMerchantServices(services) {
	if (!services) return [];

	let servicesData = services;
	if (typeof servicesData === "string") {
		try {
			servicesData = JSON.parse(servicesData);
		} catch {
			return [];
		}
	}

	if (!Array.isArray(servicesData)) return [];

	return servicesData
		.map(service => ({
			name: service.name || "",
			summary: service.summary || service.description || "",
			cover: service.cover || "",
		}))
		.filter(service => service.name || service.summary || service.cover);
}

function mapMerchantToDetail(item, merchantTypes, t, fallback = {}) {
	const hours = parseJsonField(item.business_hours).map(row => ({
		label: row.week || row.day || "",
		value: row.open && row.close ? `${row.open}-${row.close}` : row.time || "",
	}));
	const distanceKm =
		item.distance == null || Number.isNaN(Number(item.distance))
			? fallback.distanceKm
			: Number((Number(item.distance) / 1000).toFixed(2));
	const { videos } = parseMerchantMedia(item.media);
	const services = parseMerchantServices(item.services);

	return {
		id: item.id || fallback.id,
		photos: getMerchantPhotos(item),
		title: item.name || "",
		type: getMerchantTypeName(merchantTypes, item.merchant_type),
		rating: item.score || "0",
		reviews: String(item.evaluation_num ?? 0),
		address: item.address || "",
		hours,
		intro: stripHtml(item.description || ""),
		highlights: item.manager_desc ? [{ label: "", value: item.manager_desc }] : [],
		tip: t("serviceInstitution.pc.demo.detail.defaultTip"),
		contactPhone: Array.isArray(item.contact_phone) ? item.contact_phone[0] || "" : "",
		lat: item.lat,
		lng: item.lng,
		distance: formatDistance(item.distance) || fallback.distance,
		distanceKm,
		shareUrl: item.share_url || "",
		services,
		videos,
	};
}

export function useServiceInstitutionData({ t, merchantTypes, locale }) {
	const userLocationStore = useUserLocationStore();
	const pageCache = useServiceInstitutionCache();
	const activeCategoryId = ref("all");
	const selectedMerchantId = ref(null);
	const selectedRegionCode = ref("");
	const selectedRegion = ref(null);
	const regions = ref([]);
	const searchKeyword = ref("");
	const merchants = ref([]);
	const merchantDetail = ref(null);
	// 首屏从 SSR 到客户端请求完成始终保持 loading，避免先渲染空列表再闪出骨架。
	const loading = ref(true);
	const regionsLoading = ref(false);
	const detailLoading = ref(false);
	const mapCenter = ref({ lat: null, lng: null });
	let detailRequestId = 0;

	function resetRegionSelection() {
		selectedRegionCode.value = "";
		selectedRegion.value = null;
	}

	async function fetchRegions() {
		if (!import.meta.client) return [];

		regionsLoading.value = true;
		try {
			const location = await userLocationStore.ensureLocation();
			if (!location?.lat || !location?.lng) {
				regions.value = [];
				return [];
			}

			const centerLat = mapCenter.value.lat ?? location.lat;
			const centerLng = mapCenter.value.lng ?? location.lng;
			const cacheKey = getRequestCacheKey(locale, {
				lat: centerLat,
				lng: centerLng,
			});
			if (hasCachedValue(pageCache.value.regions, cacheKey)) {
				regions.value = pageCache.value.regions[cacheKey];
				return regions.value;
			}

			const response = await getMerchantRegionsByLocation({
				lat: String(centerLat),
				lng: String(centerLng),
			});

			if (response?.code === 200 && Array.isArray(response?.data)) {
				regions.value = response.data;
				pageCache.value.regions[cacheKey] = response.data;
				return response.data;
			}

			regions.value = [];
			return [];
		} catch (error) {
			console.error("fetchRegions failed", error);
			regions.value = [];
			return [];
		} finally {
			regionsLoading.value = false;
		}
	}

	async function updateMapCenterByRegion(region) {
		if (!region?.name) return;

		try {
			const response = await getCoordinates(region.name);
			const data = response?.data || response;
			if (data?.lat != null && data?.lng != null) {
				mapCenter.value = {
					lat: Number(data.lat),
					lng: Number(data.lng),
				};
			}
		} catch (error) {
			console.error("updateMapCenterByRegion failed", error);
		}
	}

	async function fetchMerchantDetail(merchantId) {
		if (!merchantId || !import.meta.client) {
			merchantDetail.value = null;
			detailLoading.value = false;
			return;
		}

		const requestId = ++detailRequestId;
		detailLoading.value = true;
		const cacheKey = getRequestCacheKey(locale, { merchantId });

		if (hasCachedValue(pageCache.value.details, cacheKey)) {
			merchantDetail.value = pageCache.value.details[cacheKey];
			detailLoading.value = false;
			return;
		}

		try {
			const response = await getMerchantShowDetail(merchantId);
			if (requestId !== detailRequestId) return;

			if (response?.code === 200 && response?.data) {
				merchantDetail.value = response.data;
				pageCache.value.details[cacheKey] = response.data;
			} else {
				merchantDetail.value = null;
			}
		} catch (error) {
			if (requestId !== detailRequestId) return;
			console.error("fetchMerchantDetail failed", error);
			merchantDetail.value = null;
		} finally {
			if (requestId === detailRequestId) {
				detailLoading.value = false;
			}
		}
	}

	async function fetchMerchants() {
		if (!import.meta.client) return;

		if (activeCategoryId.value === "nearby" && !selectedRegionCode.value) {
			merchants.value = [];
			selectedMerchantId.value = null;
			merchantDetail.value = null;
			return;
		}

		loading.value = true;
		try {
			const location = await userLocationStore.ensureLocation();
			if (!location?.lat || !location?.lng) {
				merchants.value = [];
				selectedMerchantId.value = null;
				merchantDetail.value = null;
				return;
			}

			const centerLat = mapCenter.value.lat ?? location.lat;
			const centerLng = mapCenter.value.lng ?? location.lng;
			const params = {
				lat: String(centerLat),
				lng: String(centerLng),
				self_lat: String(location.lat),
				self_lng: String(location.lng),
				merchant_type:
					activeCategoryId.value !== "all" && activeCategoryId.value !== "nearby"
						? String(activeCategoryId.value)
						: "0",
				region_code: activeCategoryId.value === "nearby" ? String(selectedRegionCode.value) : "",
			};

			const keyword = searchKeyword.value.trim();
			if (keyword) {
				params.name = keyword;
			}

			const cacheKey = getRequestCacheKey(locale, params);
			if (hasCachedValue(pageCache.value.merchants, cacheKey)) {
				merchants.value = pageCache.value.merchants[cacheKey];
				const currentSelected = merchants.value.find(item => item.id === selectedMerchantId.value);
				if (!currentSelected) {
					selectedMerchantId.value = merchants.value[0]?.id ?? null;
				}
				return;
			}

			const response = await getMerchantList(params);
			if (response?.code === 200 && Array.isArray(response?.data)) {
				merchants.value = response.data;
				pageCache.value.merchants[cacheKey] = response.data;
				const currentSelected = response.data.find(item => item.id === selectedMerchantId.value);
				if (!currentSelected) {
					selectedMerchantId.value = response.data[0]?.id ?? null;
				}
			} else {
				merchants.value = [];
				selectedMerchantId.value = null;
				merchantDetail.value = null;
			}
		} catch (error) {
			console.error("fetchMerchants failed", error);
			merchants.value = [];
			selectedMerchantId.value = null;
			merchantDetail.value = null;
		} finally {
			loading.value = false;
		}
	}

	async function setActiveCategory(categoryId) {
		if (String(categoryId) === "nearby") {
			activeCategoryId.value = "nearby";
			resetRegionSelection();
			mapCenter.value = { lat: null, lng: null };
			merchants.value = [];
			selectedMerchantId.value = null;
			merchantDetail.value = null;
			await fetchRegions();
			return;
		}

		resetRegionSelection();
		activeCategoryId.value = categoryId;
		return fetchMerchants();
	}

	async function selectRegion(region) {
		if (!region?.id) return;

		activeCategoryId.value = "nearby";
		selectedRegionCode.value = String(region.id);
		selectedRegion.value = region;
		await updateMapCenterByRegion(region);
		return fetchMerchants();
	}

	function searchMerchants(keyword) {
		searchKeyword.value = keyword;
		return fetchMerchants();
	}

	function selectMerchant(merchantId) {
		selectedMerchantId.value = merchantId;
		const merchant = merchants.value.find(item => item.id === merchantId);
		if (merchant?.lat && merchant?.lng) {
			mapCenter.value = {
				lat: Number(merchant.lat),
				lng: Number(merchant.lng),
			};
		}
	}

	const institutions = computed(() =>
		(merchants.value || []).map(item => mapMerchantToListItem(item, merchantTypes.value || [], t))
	);

	const detail = computed(() => {
		if (!merchantDetail.value) return null;
		const fallback = institutions.value.find(
			item => String(item.id) === String(selectedMerchantId.value)
		);
		return mapMerchantToDetail(merchantDetail.value, merchantTypes.value || [], t, fallback);
	});

	watch(selectedMerchantId, merchantId => {
		fetchMerchantDetail(merchantId);
	});

	watch(locale, async () => {
		if (activeCategoryId.value === "nearby") {
			await fetchRegions();
		}
		await fetchMerchants();
		if (selectedMerchantId.value) {
			fetchMerchantDetail(selectedMerchantId.value);
		}
	});

	const resultCountText = computed(() =>
		t("serviceInstitution.pc.demo.search.resultCount", { count: merchants.value.length })
	);

	onMounted(async () => {
		// 进入页面先获取 IP 定位，再用于后续列表/区域请求的 self_lat/self_lng（及默认 lat/lng）
		await userLocationStore.ensureLocation();
		await fetchMerchants();
	});

	return {
		activeCategoryId,
		selectedMerchantId,
		selectedRegionCode,
		selectedRegion,
		regions,
		regionsLoading,
		loading,
		detailLoading,
		institutions,
		detail,
		resultCountText,
		searchKeyword,
		setActiveCategory,
		selectRegion,
		selectMerchant,
		searchMerchants,
		fetchMerchants,
		fetchRegions,
	};
}
